import React, { useRef, useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { hot } from 'react-hot-loader/root';
import { ApolloProvider, Query } from 'react-apollo';
import uniqBy from 'lodash.uniqby';
import { decode } from 'html-decoder';
import ReactSVG from 'react-svg';
import chevronSmall from 'chevron-small.svg';
import building from 'building.svg';
import useSlides from '../hooks/useSlides';
import useWindowWidth from '../hooks/useWindowWidth';
import EventCard from './EventCard';
import SliderArrow from './SliderArrow';
import RentalCard from './RentalCard';
import LoadingCard from './Loading/LoadingCard';
import ListingCard from './ListingCard';
import { client } from './client';
import ConnectApollo from './ConnectApollo';

// Queries
import {
  propertiesQuery,
  eventsQuery,
  listingsQuery,
} from '../constants/queries';
import MobileCardOverflow from './MobileCardOverflow';
import { isBetween } from '../utils/isBetween';

const queries = {
  properties: propertiesQuery,
  events: eventsQuery,
  listings: listingsQuery,
};

// Used to calculate number of cards inside the slider (include 10px padding)
const cardWidths = {
  events: 220, // width 200px + (padding 10px * 2) = 220px total
  properties: 293,
  listings: 293,
  properties_small: 224,
  listings_small: 224,
};

const tokenElement = document.querySelector('[name=csrf-token]');

if (!tokenElement || !tokenElement.content) {
  console.warn('Could not find csrf-token');
}

export function CardSliderQueryComponent({
  title,
  card_type,
  display,
  link,
  query,
  is_small,
  filters = {},
}) {
  const [page, setPage] = useState(1);
  const [slideIndex, setSlideIndex] = useState(1);
  const [cards, setCards] = useState([]);
  const [sliderDisabled, setSliderDisabled] = useState(false);

  const sliderRef = useRef(null);
  const windowWidth = useWindowWidth();

  if (!cardWidths[card_type]) {
    console.error(`Card width not set for ${card_type}`);
  }

  // TODO: Add fallback for queries
  if (query && !queries[query]) {
    console.error(`Query \`${query}\` was not imported or declared in queries`);
  }

  const slideCount = useSlides({
    sliderRef,
    cardWidth: cardWidths[`${card_type}${is_small ? '_small' : ''}`],
  });

  const loadingCardsMemo = useMemo(() => {
    return [...Array(slideCount * 2)].map((_, ind) => (
      <LoadingCard key={ind} card_type={card_type} is_small={is_small} />
    ));
  }, [slideCount, card_type, is_small]);

  const sliderBreakpoint = 768;

  const variables = {
    page: '1',
    ...filters,
    listing_city_name_cont_any: filters.area_in,
  };

  return (
    <Query
      query={queries[query]}
      variables={variables}
      fetchPolicy="cache-and-network"
    >
      {({ data, loading, fetchMore }) => {
        if (data && data[query]) {
          if (data[query].collection.length) {
            if (!cards.some(cur => cur.id === data[query].collection[0].id)) {
              const scrambledCollection = [...data[query].collection].sort(
                () => {
                  if (query === 'properties') {
                    return Math.random() - 0.5;
                  }
                  return 0;
                }
              );

              const uniqCards = uniqBy(
                [...cards, ...scrambledCollection],
                'id'
              );
              setCards(uniqCards);
            }
          }
        }

        const updateQuery = (prev, { fetchMoreResult }) => {
          const uniqueCollection = [];
          const scrambledCollection = [
            ...fetchMoreResult[query].collection,
          ].sort(() => {
            if (query === 'properties') {
              return Math.random() - 0.5;
            }
            return 0;
          });
          const mergedCollection = [...cards, ...scrambledCollection];

          mergedCollection.forEach(item => {
            if (!uniqueCollection.some(uItem => uItem.id === item.id)) {
              uniqueCollection.push(item);
            }
          });

          if (fetchMoreResult[query].collection.length) {
            setCards(uniqueCollection);
          }
        };

        const buildCards = item => {
          switch (query) {
            case 'events':
              return (
                <EventCard
                  key={item.id}
                  event={item}
                  id={`${query}-card-${item.id}`}
                />
              );

            case 'properties':
              return (
                <ConnectApollo key={item.id}>
                  <RentalCard
                    id={`${query}-card-${item.id}`}
                    property={item}
                    is_small={is_small}
                  />
                </ConnectApollo>
              );

            case 'listings':
              return (
                <ListingCard
                  key={item.id}
                  listing={item}
                  id={`${query}-card-${item.id}`}
                  is_small={is_small}
                />
              );

            default:
              console.error(
                `Case for listing type \`${card_type}\` is not handled`
              );
              break;
          }
        };

        if (
          data &&
          data[query] &&
          data[query].collection.length > 0 &&
          !cards.length
        ) {
          const scrambledCollection = [...data[query].collection].sort(() => {
            if (query === 'properties') {
              return Math.random() - 0.5;
            }
            return 0;
          });
          setCards(scrambledCollection);
        }

        if (!loading) setSliderDisabled(false);

        let roundDownToNearestTenth = Math.floor(cards.length / 10) * 10;
        if (data && data[query]) {
          if (query !== 'properties') {
            roundDownToNearestTenth =
              Math.floor(data[query].total_count / 10) * 10;
          } else {
            roundDownToNearestTenth =
              Math.floor(data[query].meta.total_count / 10) * 10;
          }
        }

        let totalCardCount = Math.floor(cards.length / 10) * 10;
        if (data && data[query]) {
          if (query !== 'properties') {
            totalCardCount = data[query].total_count;
          } else {
            totalCardCount = data.properties.meta.total_count;
          }
        }

        const settings = {
          dots: false,
          infinite: false,
          speed: 500,
          slidesToShow: slideCount,
          slidesToScroll: slideCount,
          arrows: true,
          prevArrow: (
            <SliderArrow
              direction="prev"
              sliderRef={sliderRef}
              type={card_type}
            />
          ),
          nextArrow: (
            <SliderArrow
              direction="next"
              sliderRef={sliderRef}
              type={card_type}
              disabled={sliderDisabled}
            />
          ),
          beforeChange: (oldIndex, newIndex) => {
            if (oldIndex < newIndex) setSlideIndex(slideIndex + 1);
            if (oldIndex > newIndex) setSlideIndex(slideIndex - 1);
          },
          afterChange: newIndex => {
            let total_pages;
            if (data[query]) {
              total_pages =
                query === 'properties'
                  ? data[query].meta.total_pages
                  : data[query].total_pages;
            }
            const newIndexSlideCount = newIndex + slideCount;
            setSliderDisabled(false);
            if (
              isBetween(
                newIndexSlideCount,
                cards.length - slideCount,
                cards.length
              ) &&
              page < parseInt(total_pages)
            ) {
              setSliderDisabled(true);
              if (!loading) {
                fetchMore({
                  variables: { page: String(page + 1) },
                  updateQuery,
                });
                setPage(page + 1);
              }
            }
          },
        };

        return (
          <section className="slider-container" ref={sliderRef}>
            <header className="slider-header">
              <div className="title">
                <ReactSVG src={building} />
                {title && (
                  <h2>
                    <a
                      className={`secondary-text ${
                        query === 'rentals'
                          ? 'clear_rental_query_params'
                          : 'clear_listing_query_params'
                      }`}
                      href={link}
                      title={`View All ${query}`}
                      aria-label={`View All ${query}`}
                    >
                      {decode(title.split('-').join(' '))}
                    </a>
                  </h2>
                )}
              </div>
              <div className="view-all" style={{ display }}>
                {!cards.length && loading ? (
                  <div className="loading-text sub-title" />
                ) : (
                  <Fragment>
                    <a
                      className={`secondary-text ${
                        query === 'rentals'
                          ? 'clear_rental_query_params'
                          : 'clear_listing_query_params'
                      }`}
                      href={link}
                      aria-label={`View All ${query}`}
                    >
                      View All (
                      {totalCardCount < 10
                        ? cards.length
                        : `${roundDownToNearestTenth}+`}
                      )
                    </a>
                    <ReactSVG src={chevronSmall} alt="" />
                  </Fragment>
                )}
              </div>
            </header>

            {!cards.length && !loading ? (
              <div className="slider-fallback">
                <p>No {decode(title.replace(/-/g, ' '))} Available</p>
              </div>
            ) : (
              <Fragment>
                {windowWidth > sliderBreakpoint ? (
                  <Slider {...settings}>
                    {!cards.length ? loadingCardsMemo : cards.map(buildCards)}
                  </Slider>
                ) : (
                  <MobileCardOverflow
                    loading={loading}
                    cards={cards}
                    buildCards={buildCards}
                    loadingCardsMemo={loadingCardsMemo}
                    fetchMore={fetchMore}
                    updateQuery={updateQuery}
                    card_type={card_type}
                    page={page}
                    setPage={setPage}
                  />
                )}
              </Fragment>
            )}
          </section>
        );
      }}
    </Query>
  );
}

const CardSlider = ({ ...props }) => (
  <ApolloProvider client={client}>
    <CardSliderQueryComponent {...props} />
  </ApolloProvider>
);

export default hot(CardSlider);

CardSlider.propTypes = {
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  listings: PropTypes.array,
  title: PropTypes.string.isRequired,
  card_type: PropTypes.string.isRequired,
  display: PropTypes.string,
  link: PropTypes.string,
};
